import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Chip,
  Stack,
  IconButton,
  Card,
  CardContent,
  useTheme,
  alpha,
  Divider,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import type { MedicalReservationRecord } from '../../types/medicalReservation';
import { CSV_CATEGORIES, NOTIFICATION_STATUS_CONFIG } from '../../types/medicalReservation';
import { getNotificationStatus, getNotificationReasons, getPrescriptionUnavailableReasons } from '../../utils/notificationChecker';

interface DetailModalProps {
  open: boolean;
  record: MedicalReservationRecord | null;
  onClose: () => void;
}

/**
 * 診療予約レコード詳細表示モーダル
 * 
 * 意図：
 * - 56列のCSVデータを一つのテーブルに統合して表示
 * - 予約詳細情報の一覧表示
 * - 見やすい情報アーキテクチャの提供
 * 
 * 注意点：
 * - 大量情報の効率的表示
 * - モーダルサイズの最適化
 * - アクセシビリティ対応
 * 
 * 改善提案：
 * - 検索・フィルタリング機能
 * - 情報のコピー機能
 * - 印刷対応
 */
export const DetailModal: React.FC<DetailModalProps> = ({
  open,
  record,
  onClose,
}) => {
  const theme = useTheme();
  
  // アコーディオンの展開状態を管理（デフォルトは全て展開）
  const [expandedPanels, setExpandedPanels] = useState<{ [key: string]: boolean }>(
    Object.keys(CSV_CATEGORIES).reduce((acc, key) => ({ ...acc, [key]: true }), { system: true })
  );

  // パネルの展開・縮小を切り替える
  const handlePanelChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanels(prev => ({ ...prev, [panel]: isExpanded }));
  };

  const handleClose = () => {
    onClose();
  };

  // 日時フォーマット
  const formatDateTime = (dateString: string) => {
    try {
      return format(new Date(dateString), 'yyyy年MM月dd日 HH:mm:ss', { locale: ja });
    } catch {
      return dateString;
    }
  };

  if (!record) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '80vh',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={2} sx={{ minWidth: 0, flex: 1 }}>
          <PersonIcon sx={{ color: theme.palette.primary.main }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={600}>
              患者詳細情報
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {record.patientName} ({record.anybotId})
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small" aria-label="閉じる" sx={{ flexShrink: 0 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* 基本情報サマリー */}
      <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">
              担当医師
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {record.doctorInCharge}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">
              診療開始時間
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {formatDateTime(record.consultationStartTime)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">
              通知ステータス
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              {(() => {
                const status = getNotificationStatus(record);
                const config = NOTIFICATION_STATUS_CONFIG[status];
                if (status === 'none') {
                  return <Typography variant="body2">-</Typography>;
                }
                return (
                  <Chip
                    label={config.label}
                    color={config.color}
                    size="small"
                    icon={config.icon ? <span>{config.icon}</span> : undefined}
                    sx={{ 
                      backgroundColor: config.backgroundColor,
                      color: config.textColor || 'inherit'
                    }}
                  />
                );
              })()}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="caption" color="text.secondary">
              診療リスト貼り付け
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Checkbox
                checked={record.paste_flag || false}
                disabled
                size="small"
                sx={{
                  padding: 0,
                  '&.Mui-disabled': {
                    color: record.paste_flag ? theme.palette.success.main : theme.palette.action.disabled,
                  },
                }}
              />
              <Typography variant="body2" component="span" sx={{ ml: 1 }}>
                {record.paste_flag ? '完了' : '未完了'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <DialogContent sx={{ p: 3, overflow: 'auto' }}>
        <Box>
          {/* 予約詳細タイトル */}
          <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
            予約詳細
          </Typography>
          
          {/* アコーディオン形式で情報をカテゴリごとに表示 */}
          <Stack spacing={2}>
            {/* CSVカテゴリごとのアコーディオン */}
            {Object.entries(CSV_CATEGORIES).map(([categoryKey, category]) => (
              <Accordion
                key={categoryKey}
                expanded={expandedPanels[categoryKey]}
                onChange={handlePanelChange(categoryKey)}
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  '&:before': { display: 'none' },
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                    },
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    {category.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                    ({category.fields.length}項目)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {category.fields.map(field => (
                          <TableRow key={field}>
                            <TableCell
                              sx={{
                                fontWeight: 500,
                                width: '35%',
                                bgcolor: alpha(theme.palette.grey[100], 0.5),
                                borderRight: `1px solid ${theme.palette.divider}`,
                              }}
                            >
                              {field}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {record.csvData[field] || '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
            
            {/* システム情報のアコーディオン */}
            <Accordion
              expanded={expandedPanels['system']}
              onChange={handlePanelChange('system')}
              elevation={0}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                '&:before': { display: 'none' },
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.info.main, 0.08),
                  },
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  システム情報
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                  (管理用データ)
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 500,
                            width: '35%',
                            bgcolor: alpha(theme.palette.grey[100], 0.5),
                            borderRight: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          レコードID
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {record.id}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 500,
                            bgcolor: alpha(theme.palette.grey[100], 0.5),
                            borderRight: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          アップロードバッチID
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">
                            {record.upload_batch_id}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 500,
                            bgcolor: alpha(theme.palette.grey[100], 0.5),
                            borderRight: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          作成日時
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateTime(record.created_at)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          sx={{
                            fontWeight: 500,
                            bgcolor: alpha(theme.palette.grey[100], 0.5),
                            borderRight: `1px solid ${theme.palette.divider}`,
                          }}
                        >
                          最終更新日時
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDateTime(record.updated_at)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {record.assignedTo && (
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 500,
                              bgcolor: alpha(theme.palette.grey[100], 0.5),
                              borderRight: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            振り分け先
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {record.assignedTo}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {record.notes && (
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 500,
                              bgcolor: alpha(theme.palette.grey[100], 0.5),
                              borderRight: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            備考
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {record.notes}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={handleClose} variant="outlined">
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
};
